import { Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TrackedLinkDirective } from 'src/app/core/analytics/directives/tracked-link.directive';
import { AcknowledgementType } from 'src/app/core/enums/acknowledgement-type.enum';

interface TextPart {
  type: 'text' | 'link';
  content: string;
  linkType?: string;
}

@Component({
  selector: 'app-legal-acknowledgement-footer',
  templateUrl: './legal-acknowledgement-footer.component.html',
  styles: `
    p,
    a.nova-link {
      color: var(--nova-text-subtle);
    }
  `,
  imports: [RouterLink, TrackedLinkDirective],
})
export class LegalAcknowledgementFooterComponent {
  type = input<'DEFAULT' | 'ESIGNATURE'>('DEFAULT');
  buttonText = input.required<string>();

  private translationService = inject(TranslateService);

  translatedMessage = computed(() => {
    return this.translationService.instant(
      `legal-acknowledgements.MESSAGE-${this.type()}`,
      {
        buttonText: this.buttonText(),
      },
    );
  });
  messageParts = computed<TextPart[]>(() => {
    const text = this.translatedMessage();
    const parts: TextPart[] = [];

    const linkRegex = /\[\[(\w+):(.+?)\]\]/g;
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const textBefore = text.substring(lastIndex, match.index);
        if (textBefore) {
          parts.push({ type: 'text', content: textBefore });
        }
      }

      parts.push({
        type: 'link',
        content: match[2],
        linkType: match[1],
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        parts.push({ type: 'text', content: remainingText });
      }
    }

    return parts;
  });

  getAcknowledgementRoute(linkType: string): string[] {
    let acknowledgementType: string;
    switch (linkType) {
      case 'termsLink':
        acknowledgementType = AcknowledgementType.TermsOfUse;
        break;
      case 'privacyPolicyLink':
        acknowledgementType = AcknowledgementType.PrivacyPolicy;
        break;
      case 'aiDisclaimerLink':
        acknowledgementType = AcknowledgementType.AiDisclaimer;
        break;
      case 'esignatureLink':
        acknowledgementType = AcknowledgementType.ESignature;
        break;
      default:
        return ['/not-found'];
    }

    return ['/terms', acknowledgementType.toLowerCase()];
  }
}
